import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { verifyEmail, resendVerification } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import AuthLayout from '@/components/AuthLayout';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const { refreshUser, user } = useAuth();

  const [status, setStatus] = useState(token ? 'loading' : 'noToken');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await verifyEmail(token);
        setStatus('success');
        setMessage(res.data?.email || '');
        // Refresh auth state so the banner disappears if user is logged in
        try { await refreshUser(); } catch (refreshErr) {
          // Not logged in — banner refresh isn't required here.
          console.debug('refreshUser skipped after verify:', refreshErr?.message);
        }
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed');
      }
    })();
  }, [token, refreshUser]);

  const handleResend = async () => {
    try {
      await resendVerification();
      toast.success('Verification email sent — check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification email');
    }
  };

  return (
    <AuthLayout>
      <Card className="w-full shadow-2xl border-0 bg-white/95 backdrop-blur" data-testid="verify-email-page">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 text-slate-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Verifying your email…</h2>
              <p className="text-slate-500">Hang tight, this only takes a second.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2" data-testid="verify-success">Email verified</h2>
              <p className="text-slate-600 mb-6">
                {message ? <>Your email <strong>{message}</strong> is now confirmed.</> : 'Your email is now confirmed.'}
              </p>
              <Button
                onClick={() => navigate(user ? '/dashboard' : '/login')}
                className="w-full bg-[#070d1c] hover:bg-[#0c1530] text-white font-medium"
                data-testid="verify-continue-btn"
              >
                {user ? 'Go to Dashboard' : 'Sign In'}
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2" data-testid="verify-error">Verification failed</h2>
              <p className="text-slate-600 mb-6" data-testid="verify-error-message">{message}</p>
              {user && !user.email_verified && (
                <Button
                  onClick={handleResend}
                  className="w-full bg-[#070d1c] hover:bg-[#0c1530] text-white font-medium mb-3"
                  data-testid="verify-resend-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send a new verification email
                </Button>
              )}
              <Link
                to={user ? '/dashboard' : '/login'}
                className="text-sm text-slate-600 hover:text-slate-900 underline"
              >
                {user ? 'Back to Dashboard' : 'Back to Login'}
              </Link>
            </>
          )}

          {status === 'noToken' && (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="h-8 w-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Check your inbox</h2>
              <p className="text-slate-600 mb-6">
                Click the verification link we emailed you to confirm your account.
                Don&apos;t see it? Check your spam folder or request a new one below.
              </p>
              {user && !user.email_verified && (
                <Button
                  onClick={handleResend}
                  className="w-full bg-[#070d1c] hover:bg-[#0c1530] text-white font-medium mb-3"
                  data-testid="verify-resend-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Resend verification email
                </Button>
              )}
              <Link
                to={user ? '/dashboard' : '/login'}
                className="text-sm text-slate-600 hover:text-slate-900 underline"
              >
                {user ? 'Back to Dashboard' : 'Back to Login'}
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
